import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, forwardRef, Inject, Injector, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroupDirective, FormsModule, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, Observable, startWith } from 'rxjs';
import { ISelection } from 'src/app/interfaces/ISelection';
import { IRetrieveParam } from 'src/app/interfaces/ITable';
import { MaterialModule } from 'src/app/material.module';
import { ToastService } from 'src/app/services/toast.service';
import { SelectionConfig } from 'src/app/types/Select';

@Component({
    selector: 'app-autocomplete',
    imports: [MaterialModule, CommonModule, FormsModule, ReactiveFormsModule, FormsModule, TranslateModule],
    templateUrl: './autocomplete.component.html',
    styleUrl: './autocomplete.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteComponent),
            multi: true
        }
    ]
})

export class AutocompleteComponent implements OnInit, ControlValueAccessor, OnChanges {
  @Input() dataSource: ISelection[] = [];
  @Input() selectionConfig: SelectionConfig = {};
  @Input() placeholder: string = '';
  @Input() inputChanged?: (value: any) => void;
  @Input() blur?: () => void;
  @Input() focus?: () => void;
  @Input() fetchData?: (params: IRetrieveParam) => Observable<any>;
  @Input() params: IRetrieveParam = { page: 1, pageSize: 1000, sortText: '', globalSearch: '' };
  @Input() mapApiResponse?: (response: any) => any;
  @Input() class: string = '';
  @Input() defaultValue?: string;
  @Input() formControlName?: string;

  control: FormControl;
  filterInput: FormControl = new FormControl('');
  filteredOptions: Observable<ISelection[]>;
  disabled = false;
  loading = false;
  inputValue: any = '';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange = (value: any) => { };
  private onTouched = () => { };
  constructor(
    @Inject(Injector) private injector: Injector,
    private translateService: TranslateService,
    private toastService: ToastService) { }

  ngOnInit() {
    this.setFormControl();
    if (!this.fetchData) {
      this.filteredOptions = this.filterInput.valueChanges.pipe(
        startWith(''),
        map((value) => this._filter(value || ''))
      );
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fetchData']) {
      this.retrieveData();
    }
    if (changes['defaultValue'] && this.defaultValue) {
      this.inputValue = this.dataSource.find((e) => e.value == this.defaultValue || e.text == this.defaultValue)?.text;
    }
    if (changes['dataSource']) {
      this.updateDataSource(this.dataSource);
    }
  }

  writeValue(value: any) {
    if (value) {
      this.inputValue = this.dataSource.find((e) => e.value == value)?.text;
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  private _filter(value: string): ISelection[] {
    const filterValue = value?.toLowerCase() ?? '';
    return this.dataSource.filter((option) => (option?.text ?? '').toLowerCase().includes(filterValue));
  }

  setFormControl() {
    const ngControl = this.injector.get(NgControl, null);
    if (ngControl) {
      this.control = ngControl.control as FormControl;
    } else if (this.formControlName) {
      const formGroup = this.injector.get(FormGroupDirective, null);
      if (formGroup) {
        this.control = formGroup.control.get(this.formControlName) as FormControl;
      }
    } else {
      this.control = new FormControl();
    }
  }

  setDisabled(option: ISelection): boolean {
    return (
      (typeof this.selectionConfig?.disabled === 'boolean'
        ? this.selectionConfig?.disabled
        : this.selectionConfig.disabled && this.selectionConfig.disabled(option)) ?? false
    );
  }

  onSelectionChange(event: any) {
    const selectedOption = this.dataSource.find((option) => option.text === event.option.value);
    this.onChange(selectedOption?.value);
    this.inputChanged && this.inputChanged({
      inputValue: selectedOption?.text,
      value: selectedOption?.value
    });
  }

  onCancel(event: any) {
    if (!event.target.value && this.control) {
      this.control.setValue(null);
    }
  }

  hasError(): boolean {
    if (this.selectionConfig.optional && this.inputValue) {
      return this?.control?.invalid;
    } else {
      return this?.control?.invalid && this?.control?.touched;
    }
  }

  onBlur() {
    this.blur && this.blur();
    this.control?.markAsTouched();
  }

  onFocus() {
    this.focus && this.focus();
  }

  onInputChanged(event: any) {
    const value = event.target.value.trim();
    const result = this.dataSource.find((option) => option.text && value && option.text.toLowerCase() === value.toLowerCase());
    if (this.selectionConfig.optional && !result) {
      this.onChange(value);
    } else {
      this.onChange(result?.value);
    }
    this.filterInput.setValue(value);
    this.inputChanged && this.inputChanged(
      {
        inputValue: value,
        value: result?.value
      });
  }

  retrieveData() {
    if (this.fetchData) {
      const reqParams = { ...this.params };
      this.dataSource = [];
      this.loading = true;
      this.fetchData(reqParams).subscribe({
        next: (response) => {
          let dataMapApi;
          if (this.mapApiResponse) {
            dataMapApi = this.mapApiResponse(response.data ?? response);
          }
          this.dataSource = (dataMapApi ?? response.data ?? response).map((apiData: any) => ({
            value: apiData[this.selectionConfig.key || ''],
            text: apiData[this.selectionConfig.title || ''],
            group: apiData[this.selectionConfig.group || ''],
            disabled:
              typeof this.selectionConfig?.disabled === 'boolean'
                ? this.selectionConfig?.disabled
                : this.selectionConfig.disabled && this.selectionConfig.disabled(apiData)
          }));
          this.loading = false;
          this.filteredOptions = this.filterInput.valueChanges.pipe(
            startWith(''),
            map((value) => this._filter(value || ''))
          );
          if (this.control.value) {
            this.inputValue = this.dataSource.find((e) => e.value == this.control.value)?.text;
          }
        },
        error: ({ error }: HttpErrorResponse) => {
          this.toastService.error(this.translateService.instant(error));
          this.loading = false;
        }
      });
    }
  }

  updateDataSource(newData: ISelection[]) {
    this.dataSource = newData;
    const currentFilter = this.filterInput.value || '';
    this.filteredOptions = this.filterInput.valueChanges.pipe(
      startWith(currentFilter),
      map((value) => this._filter(value || ''))
    );
  }

  getClass(): string {
    return this.class;
  }
}
